import { DndContext, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { FC, memo, useState } from 'react';
import { IBranchPaymentTypeOption } from '../../types/branchEdit';
import PaymentTypeCheckboxOption from '../PaymentTypeCheckboxOption/PaymentTypeCheckboxOption';
import { localeStore } from 'src/mobx/localesStore';
import { DndSensors, dndAutoScrollOptionsDefault } from 'src/utils/dnd';
import { swapArrayItemsByIndexes } from 'src/utils/helpers';
import Text, { TEXT } from 'src/view/components/Text/Text';
import TooltipIcon from '~/view/components/TooltipIcon/TooltipIcon';

interface IProps {
  paymentTypes: IBranchPaymentTypeOption[];
  isValid: boolean;
  titled?: boolean;
  noDefault?: boolean;
  onChange: (types: IBranchPaymentTypeOption[]) => void;
}

const PaymentMethods: FC<IProps> = ({ paymentTypes, isValid, titled, noDefault, onChange }): JSX.Element => {
  const [draggingElem, setDraggingElem] = useState<{ id: string; data: { option: IBranchPaymentTypeOption; index: number } } | null>(null);

  const dndSensors = DndSensors();

  const onChangeStatus = (paymentTypeId: string | number) => {
    const updatedPaymentTypes = paymentTypes.map((paymentType) => {
      if (paymentType.id !== paymentTypeId) return paymentType;
      return { ...paymentType, status: !paymentType.status };
    });
    onChange(updatedPaymentTypes);
  };

  const onDragStart = ({ active }: DragStartEvent) => {
    setDraggingElem({ id: active.id, data: { option: active.data.current?.option, index: active.data.current?.index } });
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    const activeElemIndex = active.data.current?.index;
    const overElemIndex = over?.data.current?.index;

    const resortedPaymentTypes = swapArrayItemsByIndexes(paymentTypes, activeElemIndex, overElemIndex);
    onChange(resortedPaymentTypes);
  };

  const onDragCancel = () => setDraggingElem(null);

  return (
    <div className="d-flex flex-column">
      {titled && (
        <div className="d-flex align-items-center mb-4">
          <p className="family-semibold text-size-18 mr-2 required">{localeStore.t('payment_method')}</p>
          {!noDefault && <TooltipIcon message="pay_method_default_infotip" />}
        </div>
      )}

      <DndContext
        id="payment"
        autoScroll={dndAutoScrollOptionsDefault}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        sensors={dndSensors}
        onDragStart={onDragStart}
        onDragCancel={onDragCancel}
        onDragEnd={onDragEnd}
      >
        <div className="d-flex flex-column gap-20 w-fit-content">
          {paymentTypes.map((paymentType, idx) => (
            <PaymentTypeCheckboxOption
              id={paymentType.id}
              key={paymentType.id}
              option={paymentType}
              index={idx}
              withDefaultLabel={!noDefault && idx === 0}
              error={!isValid}
              onClick={onChangeStatus}
            />
          ))}
        </div>

        <DragOverlay>
          {draggingElem ? (
            <PaymentTypeCheckboxOption
              id={10}
              key={10}
              option={draggingElem.data.option}
              index={draggingElem.data.index}
              withDefaultLabel={!noDefault && draggingElem.data.index === 0}
              error={!isValid}
              className="active"
              onClick={onChangeStatus}
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {!isValid && <Text type={TEXT.ERROR} text="atleast_one_payment_method" className="max-width-fit mt-2 ml-4" />}
    </div>
  );
};

export default memo(PaymentMethods);
